import React, { useContext, useState, useEffect } from "react";
import HeaderAndFilters from "../components/HeadersAndFilters";
import ListItems from "../components/ListItems";
import AddNewRisk from "../components/modals/AddNewRisk";
import { OrganizationContext } from "../contexts/OrganizationContextProvider";
import { ProjectContext } from "../contexts/ProjectContextProvider";
import { getAllRisksWithFilters, deleteRisk } from "../api/Api";
import { showToast } from "../utils/toastUtils";
import { MdOutlineHub } from "react-icons/md";
import styles from "./Risk.module.css";
import UpdateRiskModel from "../components/modals/UpdateRiskModel";
import Box from "../components/ui/Box";
import BreadCrumb from "./../components/BreadCrumb";
import { FiAlertTriangle } from "react-icons/fi";

function Risk({
  showHeader = true,
  showProjectButton = true,
  showWorkstreamButton = true,
}) {
  const { data: organizationData } = useContext(OrganizationContext);
  const { data: projectData } = useContext(ProjectContext);
  const organizationId = organizationData?.current_organization?.id;

  const [risks, setRisks] = useState([]);
  const [filteredRisks, setFilteredRisks] = useState([]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [showAddRiskModal, setShowAddRiskModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showUpdateRiskModal, setShowUpdateRiskModal] = useState(false);

  /** ------------------------ Project Dropdown Options ------------------------ **/
  const projectOptions =
    projectData?.map((project) => ({
      name: project?.name, // Display name in dropdown
      id: project?.id, // Project ID for filtering
    })) || [];

  //fetchRisks function to get risks based on filters
  const fetchRisks = async () => {
    if (!organizationId) return;

    try {
      // Get all risks to extract unique users for dropdown
      const allRisksResponse = await getAllRisksWithFilters(
        organizationId,
        null,
        null,
        filters.project
      );

      if (allRisksResponse.status !== 200) {
        showToast(allRisksResponse.message || "Failed to fetch risks", "error");
        return;
      }

      // Extract createdByOptions from all risks
      const uniqueCreatedBy = [
        ...new Set(
          allRisksResponse.data.map((risk) => JSON.stringify(risk.created_by))
        ),
      ];
      const createdByOptions = uniqueCreatedBy.map((userStr) => {
        const user = JSON.parse(userStr);
        return {
          name: `${user.first_name} ${user.last_name}`,
          id: user.id,
        };
      });

      // Get filtered risks (by user/project)
      const filteredResponse = await getAllRisksWithFilters(
        organizationId,
        null,
        filters.user,
        filters.project
      );

      if (filteredResponse.status === 200) {
        setRisks(filteredResponse.data);
        setFilters((prevFilters) => ({
          ...prevFilters,
          createdByOptions,
        }));
      } else {
        showToast(
          filteredResponse.message || "Failed to fetch filtered risks",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching risks:", error);
      showToast("Failed to fetch risks", "error");
      setError("Failed to fetch risks. Please try again later.");
    }
  };

  // Refresh function that re-fetches the risks
  const refresh = () => {
    fetchRisks();
  };

  useEffect(() => {
    fetchRisks();
  }, [organizationId, filters.user, filters.project]);

  useEffect(() => {
    if (!filters.search) {
      setFilteredRisks(risks);
      return;
    }

    const filtered = risks.filter((risk) => {
      const title = risk.title?.toLowerCase() || "";
      const description = risk.description?.toLowerCase() || "";
      return (
        title.includes(filters.search.toLowerCase()) ||
        description.includes(filters.search.toLowerCase())
      );
    });

    setFilteredRisks(filtered);
  }, [filters.search, risks]);

  const handleDelete = async (riskId) => {
    try {
      const response = await deleteRisk(riskId);
      if (response.status === 200) {
        showToast("Risk deleted successfully", "success");
        setRisks((prevRisks) => prevRisks.filter((risk) => risk.id !== riskId));
        refresh();
      } else {
        showToast(response.message || "Failed to delete risk", "error");
      }
    } catch (error) {
      console.error("Error deleting risk:", error);
      showToast("Failed to delete risk", "error");
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  const openAddRiskModal = () => setShowAddRiskModal(true);
  const closeAddRiskModal = () => setShowAddRiskModal(false);

  const openUpdateRiskModal = (risk) => {
    setSelectedRisk(risk);
    setShowUpdateRiskModal(true);
  };

  const closeUpdateRiskModal = () => {
    setSelectedRisk(null);
    setShowUpdateRiskModal(false);
  };

  const breadcrumbArray = [
    { title: "Learning Hub", link: "/learning-hub" },
    { title: "Risks", link: "/risks" },
  ];

  return (
    <Box>
      <BreadCrumb
        className={styles.breadCrumb}
        array={breadcrumbArray}
        icon={<MdOutlineHub size={24} color={"cyan"} />}
      />
      <Box className="bg-white ">
        <HeaderAndFilters
          filters={filters}
          showHeader={showHeader}
          headerIcon={<FiAlertTriangle size={28} />}
          headerTitle="Risks List"
          projectOptions={projectOptions}
          workstreamOptions={[
            { label: "Workstream 1", value: 1 },
            { label: "Workstream 2", value: 2 },
          ]}
          createdByOptions={filters.createdByOptions || []}
          handleClick={openAddRiskModal}
          onFilterChange={handleFilterChange}
          newButtonText="NEW RISK"
          searchPlaceholder="Search..."
          showProjectButton={showProjectButton}
          showWorkstreamButton={false}
          showCreatedByButton={true}
        />

        {/* Risk List */}
        <ListItems
          items={filteredRisks}
          onEdit={openUpdateRiskModal}
          onDelete={handleDelete}
          titleField="title"
          descriptionField="description"
          dateField="created_at"
          avatarField="created_by"
          noItemsMessage='No risks to display. To add a new risk, click on the "NEW RISK" button.'
        />
      </Box>

      {/* Add Risk Modal */}
      <AddNewRisk
        show={showAddRiskModal}
        handleClose={closeAddRiskModal}
        refresh={refresh}
      />
      {selectedRisk && (
        <UpdateRiskModel
          show={showUpdateRiskModal}
          handleClose={closeUpdateRiskModal}
          risk={selectedRisk}
          setRisks={setRisks}
          refresh={refresh}
        />
      )}
    </Box>
  );
}

export default Risk;
